/* BuyerDeveloperPortal — in-house developer portal where buyers manage
 * their API keys, pricing rules, webhooks, and connectors.
 *
 * Five tabs (all on one page so it feels like a single dashboard):
 *   1. Overview     — landing + connected suppliers + quick links
 *   2. API keys     — list / create / revoke per-supplier keys
 *   3. Pricing      — margin rules editor (per-supplier)
 *   4. Webhooks     — subscriptions + delivery log
 *   5. Docs         — interactive endpoint reference + code samples
 *
 * Mounted at /buyer/developers.
 */
import { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../../components/ui/dialog";
import { PageHeader } from "../../components/PortalShell";
import {
  Code, Key, Webhook, ShoppingBag, BookOpen, Plus, Trash2, Copy,
  AlertTriangle, CheckCircle2, ExternalLink, Eye, EyeOff, RefreshCcw,
  Activity, Zap, AlertCircle, Building2, ChevronRight, X, Save,
  Terminal, Play, RefreshCw, Loader2,
} from "lucide-react";

const TABS = [
  { id: "overview",  label: "Overview",  icon: Code },
  { id: "keys",      label: "API keys",  icon: Key },
  { id: "playground", label: "Playground", icon: Terminal },
  { id: "pricing",   label: "Pricing rules", icon: Activity },
  { id: "webhooks",  label: "Webhooks",  icon: Webhook },
  { id: "shopify",   label: "Shopify",   icon: ShoppingBag },
  { id: "docs",      label: "Docs",      icon: BookOpen },
];

export default function BuyerDeveloperPortal({ embedded = false } = {}) {
  const [tab, setTab] = useState(() => {
    try { return localStorage.getItem("verqo:devportal:tab") || "overview"; } catch (_e) { return "overview"; }
  });
  useEffect(() => { try { localStorage.setItem("verqo:devportal:tab", tab); } catch (_e) { /* ignore */ } }, [tab]);

  const [connections, setConnections] = useState([]);
  useEffect(() => {
    api.get("/buyer/connections").then(r => {
      const arr = r.data?.connections || r.data || [];
      setConnections(arr.filter(c => c.status === "active"));
    }).catch(() => {});
  }, []);

  return (
    <>
      {!embedded && (
        <PageHeader title="Integrations & API" subtitle="Connect your supplier catalogs to your ERP · Shopify · QuickBooks · Zapier · custom API"
          action={
            <a href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/v1/integrations/openapi.json`}
               target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full border border-border text-xs hover:border-foreground/40">
              <Code className="w-3.5 h-3.5"/>OpenAPI spec
            </a>
          }/>
      )}

      <div className={`${embedded ? "py-2" : "px-8 py-3 border-b border-border bg-white sticky top-0 z-10"} overflow-x-auto`}>
        <div className="inline-flex gap-1 text-xs">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              data-testid={`dev-tab-${t.id}`}
              className={`inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full transition-colors ${tab === t.id ? "bg-foreground text-background" : "text-foreground/65 hover:text-foreground"}`}>
              <t.icon className="w-3.5 h-3.5"/>{t.label}
            </button>
          ))}
        </div>
        {embedded && (
          <a href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/v1/integrations/openapi.json`}
             target="_blank" rel="noreferrer"
             className="ml-2 inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full border border-border text-xs hover:border-foreground/40 align-middle"
             data-testid="dev-openapi-link-embedded">
            <Code className="w-3.5 h-3.5"/>OpenAPI spec
          </a>
        )}
      </div>

      <div className={embedded ? "pt-4" : "page-shell"}>
        {tab === "overview"   && <OverviewPanel connections={connections} onJump={setTab}/>}
        {tab === "keys"       && <KeysPanel connections={connections}/>}
        {tab === "playground" && <PlaygroundPanel connections={connections}/>}
        {tab === "pricing"    && <PricingPanel connections={connections}/>}
        {tab === "webhooks"   && <WebhooksPanel connections={connections}/>}
        {tab === "shopify"    && <ShopifyPanel connections={connections}/>}
        {tab === "docs"       && <DocsPanel/>}
      </div>
    </>
  );
}

/* ─────────────────────── Overview tab ────────────────────────── */
function OverviewPanel({ connections, onJump }) {
  return (
    <div className="space-y-6" data-testid="dev-overview">
      <div className="rounded-2xl border border-border bg-gradient-to-br from-foreground to-foreground/90 text-background p-6">
        <div className="text-[10px] mono uppercase tracking-[0.22em] opacity-65">Verqo Catalog API</div>
        <div className="display text-2xl mt-2">Bring your suppliers&apos; catalogs into your ERP</div>
        <p className="text-sm opacity-80 mt-2 max-w-2xl leading-relaxed">
          Pull live products, prices, and stock — apply your own margins — submit POs back. One REST API, per-supplier keys, real-time webhooks.
        </p>
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Connected suppliers" value={connections.length}/>
          <Stat label="API base" value="/api/v1/integrations" mono/>
          <Stat label="Format" value="REST · JSON"/>
          <Stat label="Webhooks" value="HMAC-signed"/>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <QuickCard icon={Key} title="Create your first API key" desc="One key per supplier connection. Scope-limited." testid="quick-keys" tab="keys" onJump={onJump}/>
        <QuickCard icon={Activity} title="Set your margin rules" desc="Per product, per category, or catch-all." testid="quick-pricing" tab="pricing" onJump={onJump}/>
        <QuickCard icon={Webhook} title="Subscribe to webhooks" desc="Real-time product + order events." testid="quick-webhooks" tab="webhooks" onJump={onJump}/>
        <QuickCard icon={ShoppingBag} title="Connect Shopify" desc="Mirror your supplier&apos;s catalog into Shopify." testid="quick-shopify" tab="shopify" onJump={onJump}/>
        <QuickCard icon={BookOpen} title="Read the docs" desc="Endpoints + code samples." testid="quick-docs" tab="docs" onJump={onJump}/>
      </div>
    </div>
  );
}

function Stat({ label, value, mono }) {
  return (
    <div className="rounded-xl bg-white/10 p-3">
      <div className="text-[9.5px] mono uppercase tracking-wider opacity-70">{label}</div>
      <div className={`mt-1 text-sm ${mono ? "mono" : "font-medium"}`}>{value}</div>
    </div>
  );
}

function QuickCard({ icon: Icon, title, desc, testid, tab, onJump }) {
  return (
    <button
      onClick={() => onJump && onJump(tab)}
      data-testid={testid}
      className="text-left rounded-2xl border border-border bg-white p-5 hover:border-foreground/40 transition-colors">
      <span className="w-10 h-10 rounded-lg bg-foreground text-background grid place-items-center"><Icon className="w-5 h-5"/></span>
      <div className="display text-base mt-3">{title}</div>
      <div className="text-[12px] text-foreground/60 mt-1 leading-relaxed">{desc}</div>
      <div className="mt-3 inline-flex items-center gap-1 text-[11.5px] text-foreground/70">Open <ChevronRight className="w-3 h-3"/></div>
    </button>
  );
}

/* ─────────────────────── API keys tab ─────────────────────────── */
function KeysPanel({ connections }) {
  const [keys, setKeys] = useState([]);
  const [newKey, setNewKey] = useState(null);     // freshly created key (shown once)
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState({ seller_id: "", name: "API key", env: "live" });

  const load = () => api.get("/buyer/api/keys").then(r => setKeys(r.data?.keys || []));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!creating.seller_id) return toast.error("Pick a supplier");
    try {
      const r = await api.post("/buyer/api/keys", creating);
      setNewKey(r.data);
      setCreateOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create key"); }
  };

  const revoke = async (kid) => {
    if (!window.confirm("Revoke this API key? Any integration using it will stop working immediately.")) return;
    try { await api.delete(`/buyer/api/keys/${kid}`); load(); toast.success("Key revoked"); }
    catch { toast.error("Revoke failed"); }
  };

  return (
    <div className="space-y-4" data-testid="dev-keys">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="display text-xl">API keys</h2>
          <p className="text-[12px] text-foreground/55">One key per supplier connection. Use the bearer token in your <span className="mono">Authorization</span> header.</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} data-testid="dev-create-key"
          className="rounded-full bg-foreground text-background hover:bg-foreground/90">
          <Plus className="w-4 h-4 mr-1"/>New key
        </Button>
      </div>

      <div className="rounded-2xl border border-border bg-white overflow-hidden">
        {keys.length === 0 ? (
          <div className="p-12 text-center text-foreground/45 text-sm">
            <Key className="w-7 h-7 mx-auto opacity-50"/>
            <div className="mt-3">No API keys yet. Create one to start integrating.</div>
          </div>
        ) : (
          <table className="w-full text-[12.5px]">
            <thead className="bg-secondary/50 border-b border-border">
              <tr className="text-foreground/55">
                <Th>Name</Th><Th>Supplier</Th><Th>Key prefix</Th><Th>Env</Th><Th right>Used</Th><Th>Last used</Th><Th right>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {keys.map(k => {
                const supplier = connections.find(c => c.seller_id === k.seller_id);
                // Sandbox keys bypass the seller gate, so only flag live keys
                // whose seller has paused the API.
                const paused = k.env !== "test" && k.seller_api_enabled === false;
                return (
                  <tr key={k.id} className="border-b last:border-b-0 border-border" data-testid={`key-row-${k.id}`}>
                    <Td>
                      {k.name}
                      {k.revoked_at && <span className="ml-2 chip bg-rose-50 border border-rose-200 text-rose-700 text-[10px]">revoked</span>}
                      {paused && !k.revoked_at && (
                        <span
                          data-testid={`key-paused-badge-${k.id}`}
                          title="The supplier has paused their Catalog API. Your key is valid but every request returns 403 until they re-enable it."
                          className="ml-2 chip bg-amber-50 border border-amber-200 text-amber-800 text-[10px] inline-flex items-center gap-1">
                          <AlertCircle className="w-3 h-3"/>supplier paused
                        </span>
                      )}
                    </Td>
                    <Td>{supplier?.tenant_name || k.seller_id.slice(0,8)}</Td>
                    <Td><span className="mono">{k.key_prefix}…{k.last_four}</span></Td>
                    <Td><span className={`chip text-[10px] ${k.env === "test" ? "bg-amber-50 border-amber-200 text-amber-800" : "bg-secondary"}`}>{k.env}</span></Td>
                    <Td right><span className="mono">{k.request_count || 0}</span></Td>
                    <Td>{k.last_used_at ? new Date(k.last_used_at).toLocaleString() : "never"}</Td>
                    <Td right>
                      {!k.revoked_at && (
                        <button onClick={() => revoke(k.id)} className="text-rose-600 hover:bg-rose-50 rounded-md p-1.5"><Trash2 className="w-3.5 h-3.5"/></button>
                      )}
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>New API key</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Supplier</Label>
              <select value={creating.seller_id} onChange={(e) => setCreating(c => ({ ...c, seller_id: e.target.value }))}
                className="mt-1.5 w-full h-10 rounded-md border border-input bg-white px-3 text-sm" data-testid="new-key-supplier">
                <option value="">— pick a supplier —</option>
                {connections.map(c => <option key={c.seller_id} value={c.seller_id}>{c.tenant_name}</option>)}
              </select>
            </div>
            <div>
              <Label>Name</Label>
              <Input value={creating.name} onChange={(e) => setCreating(c => ({ ...c, name: e.target.value }))} placeholder="e.g. My QuickBooks integration" className="mt-1.5"/>
            </div>
            <div>
              <Label>Environment</Label>
              <select value={creating.env} onChange={(e) => setCreating(c => ({ ...c, env: e.target.value }))}
                className="mt-1.5 w-full h-10 rounded-md border border-input bg-white px-3 text-sm">
                <option value="live">Live</option>
                <option value="test">Test (sandbox)</option>
              </select>
            </div>
          </div>
          <Button onClick={create} data-testid="dev-create-key-submit" className="w-full mt-2 bg-foreground text-background rounded-full">Create key</Button>
        </DialogContent>
      </Dialog>

      {/* Show-once dialog after creation */}
      <Dialog open={!!newKey} onOpenChange={(v) => !v && setNewKey(null)}>
        <DialogContent className="max-w-lg" data-testid="new-key-shown-once">
          <DialogHeader>
            <DialogTitle className="inline-flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-emerald-600"/>API key created</DialogTitle>
            <DialogDescription>
              Copy this key now — for security, you won&apos;t be able to see it again.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-lg bg-foreground text-background p-4 mono text-[12px] break-all">{newKey?.full_key}</div>
          <Button onClick={() => { navigator.clipboard.writeText(newKey?.full_key || ""); toast.success("Copied to clipboard"); }}
            className="w-full rounded-full bg-foreground text-background"><Copy className="w-3.5 h-3.5 mr-1.5"/>Copy</Button>
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-[11.5px] text-amber-900 mt-1">
            <AlertTriangle className="inline w-3 h-3 mr-1"/>This is the only time the full key is shown. We only store a hash on our side.
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ─────────────────────── Pricing rules tab ─────────────────── */
function PricingPanel({ connections }) {
  const [supplierId, setSupplierId] = useState("");
  const [rules, setRules] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (connections.length > 0 && !supplierId) setSupplierId(connections[0].seller_id);
  }, [connections, supplierId]);
  useEffect(() => {
    if (!supplierId) return;
    api.get(`/buyer/api/pricing/${supplierId}`).then(r => setRules(r.data?.rules || []));
  }, [supplierId]);

  const addRule = () => setRules([...rules, { match: { any: true }, markup_pct: 35, round_to: ".99" }]);
  const updateRule = (i, patch) => setRules(rules.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const removeRule = (i) => setRules(rules.filter((_, idx) => idx !== i));

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/buyer/api/pricing/${supplierId}`, { rules });
      toast.success("Pricing rules saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="dev-pricing">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="display text-xl">Margin rules</h2>
          <p className="text-[12px] text-foreground/55">Per-supplier resale margins applied server-side when you call <span className="mono">?with_margin=1</span>. First match wins; order rules from most-specific to most-general.</p>
        </div>
        <select value={supplierId} onChange={(e) => setSupplierId(e.target.value)}
          className="h-10 rounded-md border border-border bg-white px-3 text-sm">
          {connections.map(c => <option key={c.seller_id} value={c.seller_id}>{c.tenant_name}</option>)}
        </select>
      </div>

      <div className="space-y-3">
        {rules.map((r, i) => (
          <RuleCard key={i} rule={r} update={(p) => updateRule(i, p)} remove={() => removeRule(i)} index={i}/>
        ))}
        <button onClick={addRule} data-testid="add-pricing-rule"
          className="w-full h-11 rounded-xl border border-dashed border-foreground/30 hover:border-foreground/60 text-sm inline-flex items-center justify-center gap-1.5 text-foreground/65 hover:text-foreground">
          <Plus className="w-3.5 h-3.5"/>Add rule
        </button>
      </div>

      <div className="flex justify-end">
        <Button onClick={save} disabled={busy} data-testid="save-pricing-rules"
          className="rounded-full bg-foreground text-background">
          <Save className="w-3.5 h-3.5 mr-1.5"/>{busy ? "Saving…" : "Save rules"}
        </Button>
      </div>
    </div>
  );
}

function RuleCard({ rule, update, remove, index }) {
  const matchKey = rule.match?.any ? "any" : (rule.match?.sku !== undefined ? "sku" : rule.match?.category !== undefined ? "category" : "any");
  return (
    <div className="rounded-xl border border-border bg-white p-3.5 grid sm:grid-cols-[1fr_1fr_auto_auto] gap-3 items-start" data-testid={`rule-${index}`}>
      <div>
        <Label className="text-[10px] mono uppercase tracking-wider text-foreground/55">Match</Label>
        <select value={matchKey} onChange={(e) => {
            const k = e.target.value;
            update({ match: k === "any" ? { any: true } : k === "sku" ? { sku: "" } : { category: "" } });
          }}
          className="mt-1 w-full h-9 rounded-md border border-input bg-white px-2 text-[12.5px]">
          <option value="any">All products (catch-all)</option>
          <option value="sku">A specific SKU</option>
          <option value="category">A category</option>
        </select>
        {matchKey === "sku" && (
          <Input value={rule.match.sku} onChange={(e) => update({ match: { sku: e.target.value } })} placeholder="SKU" className="mt-1.5 h-9 mono"/>
        )}
        {matchKey === "category" && (
          <Input value={rule.match.category} onChange={(e) => update({ match: { category: e.target.value } })} placeholder="Category" className="mt-1.5 h-9"/>
        )}
      </div>
      <div>
        <Label className="text-[10px] mono uppercase tracking-wider text-foreground/55">Pricing</Label>
        <div className="grid grid-cols-2 gap-1.5 mt-1">
          <div>
            <Input type="number" step="0.1" value={rule.markup_pct ?? ""} onChange={(e) => update({ markup_pct: e.target.value === "" ? null : parseFloat(e.target.value), fixed_price: null })} placeholder="% markup" className="h-9 mono"/>
          </div>
          <div>
            <Input type="number" step="0.01" value={rule.fixed_price ?? ""} onChange={(e) => update({ fixed_price: e.target.value === "" ? null : parseFloat(e.target.value), markup_pct: null })} placeholder="$ fixed" className="h-9 mono"/>
          </div>
        </div>
      </div>
      <div>
        <Label className="text-[10px] mono uppercase tracking-wider text-foreground/55">Round</Label>
        <select value={rule.round_to || ""} onChange={(e) => update({ round_to: e.target.value || null })}
          className="mt-1 w-full h-9 rounded-md border border-input bg-white px-2 text-[12.5px]">
          <option value="">No rounding</option>
          <option value=".99">.99</option>
          <option value=".95">.95</option>
          <option value="1.00">whole $</option>
        </select>
      </div>
      <button onClick={remove} className="text-foreground/40 hover:text-rose-600 mt-6"><Trash2 className="w-4 h-4"/></button>
    </div>
  );
}

/* ─────────────────────── Webhooks tab ────────────────────────── */
function WebhooksPanel({ connections }) {
  const [subs, setSubs] = useState([]);
  const [events, setEvents] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [newSecret, setNewSecret] = useState(null);
  const [keys, setKeys] = useState([]);
  const [draft, setDraft] = useState({ seller_id: "", api_key_id: "", url: "", events: [] });

  const load = () => api.get("/buyer/api/webhooks").then(r => { setSubs(r.data?.webhooks || []); setEvents(r.data?.available_events || []); });
  useEffect(() => { load(); api.get("/buyer/api/keys").then(r => setKeys(r.data?.keys || [])); }, []);

  const create = async () => {
    if (!draft.url.startsWith("https://")) return toast.error("URL must use https://");
    try {
      const r = await api.post("/buyer/api/webhooks", draft);
      setNewSecret(r.data?.secret);
      setCreateOpen(false);
      setDraft({ seller_id: "", api_key_id: "", url: "", events: [] });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Create failed"); }
  };
  const del = async (sid) => {
    if (!window.confirm("Delete this webhook?")) return;
    try { await api.delete(`/buyer/api/webhooks/${sid}`); load(); }
    catch { toast.error("Delete failed"); }
  };

  const sellerKeys = keys.filter(k => k.seller_id === draft.seller_id && !k.revoked_at);

  return (
    <div className="space-y-4" data-testid="dev-webhooks">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="display text-xl">Webhooks</h2>
          <p className="text-[12px] text-foreground/55">Real-time push of catalog + order events. HMAC-signed (verify with the per-subscription secret).</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} className="rounded-full bg-foreground text-background">
          <Plus className="w-4 h-4 mr-1"/>New webhook
        </Button>
      </div>

      <div className="rounded-2xl border border-border bg-white overflow-hidden">
        {subs.length === 0 ? (
          <div className="p-12 text-center text-foreground/45 text-sm">
            <Webhook className="w-7 h-7 mx-auto opacity-50"/>
            <div className="mt-3">No webhook subscriptions yet.</div>
          </div>
        ) : (
          <table className="w-full text-[12.5px]">
            <thead className="bg-secondary/50 border-b border-border">
              <tr className="text-foreground/55"><Th>URL</Th><Th>Events</Th><Th>Status</Th><Th>Last</Th><Th right>Actions</Th></tr>
            </thead>
            <tbody>
              {subs.map(s => (
                <tr key={s.id} className="border-b last:border-b-0 border-border">
                  <Td><span className="mono text-[11.5px] truncate block max-w-md">{s.url}</span></Td>
                  <Td><span className="text-[11px] text-foreground/65">{s.events.length === 0 ? "All events" : `${s.events.length} events`}</span></Td>
                  <Td>{s.enabled ? <span className="chip bg-emerald-50 border-emerald-200 text-emerald-700 text-[10px]">enabled</span> : <span className="chip bg-rose-50 border-rose-200 text-rose-700 text-[10px]">disabled</span>}</Td>
                  <Td><span className="text-[11px] text-foreground/55">{s.last_delivered_at ? new Date(s.last_delivered_at).toLocaleString() : "never"}</span></Td>
                  <Td right><button onClick={() => del(s.id)} className="text-rose-600 hover:bg-rose-50 rounded-md p-1.5"><Trash2 className="w-3.5 h-3.5"/></button></Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>New webhook</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Supplier</Label>
              <select value={draft.seller_id} onChange={(e) => setDraft(d => ({ ...d, seller_id: e.target.value, api_key_id: "" }))}
                className="mt-1.5 w-full h-10 rounded-md border border-input bg-white px-3 text-sm">
                <option value="">— pick a supplier —</option>
                {connections.map(c => <option key={c.seller_id} value={c.seller_id}>{c.tenant_name}</option>)}
              </select>
            </div>
            <div>
              <Label>API key</Label>
              <select value={draft.api_key_id} onChange={(e) => setDraft(d => ({ ...d, api_key_id: e.target.value }))}
                className="mt-1.5 w-full h-10 rounded-md border border-input bg-white px-3 text-sm">
                <option value="">— pick a key —</option>
                {sellerKeys.map(k => <option key={k.id} value={k.id}>{k.name} ({k.key_prefix}…{k.last_four})</option>)}
              </select>
            </div>
            <div>
              <Label>URL (must be https://)</Label>
              <Input value={draft.url} onChange={(e) => setDraft(d => ({ ...d, url: e.target.value }))} placeholder="https://api.your-erp.com/webhooks/verqo" className="mt-1.5 mono text-[12.5px]"/>
            </div>
            <div>
              <Label>Events</Label>
              <div className="mt-1.5 max-h-40 overflow-y-auto rounded border border-border bg-secondary/30 p-2 grid grid-cols-1 gap-1 text-[11.5px]">
                {events.map(e => {
                  const on = draft.events.includes(e);
                  return (
                    <label key={e} className="inline-flex items-center gap-1.5 cursor-pointer">
                      <input type="checkbox" checked={on}
                        onChange={(ev) => setDraft(d => ({ ...d, events: ev.target.checked ? [...d.events, e] : d.events.filter(x => x !== e) }))}
                        className="accent-foreground"/>
                      <span className="mono">{e}</span>
                    </label>
                  );
                })}
              </div>
              <p className="text-[10.5px] text-foreground/55 mt-1">Empty selection = all events.</p>
            </div>
          </div>
          <Button onClick={create} className="w-full mt-2 bg-foreground text-background rounded-full">Create webhook</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!newSecret} onOpenChange={(v) => !v && setNewSecret(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Save your webhook secret</DialogTitle></DialogHeader>
          <div className="rounded-lg bg-foreground text-background p-4 mono text-[12px] break-all">{newSecret}</div>
          <Button onClick={() => { navigator.clipboard.writeText(newSecret || ""); toast.success("Copied"); }} className="w-full bg-foreground text-background rounded-full">
            <Copy className="w-3.5 h-3.5 mr-1.5"/>Copy
          </Button>
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-[11.5px] text-amber-900">
            <AlertTriangle className="inline w-3 h-3 mr-1"/>Use this secret to verify the HMAC-SHA256 signature on every delivery. Stored on your side only.
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ─────────────────────── Shopify connector tab ─────────────── */
function ShopifyPanel({ connections }) {
  const [supplierId, setSupplierId] = useState("");
  const [config, setConfig] = useState({ shop_domain: "", access_token: "" });
  const [oauthShop, setOauthShop] = useState("");
  const [installed, setInstalled] = useState(null);
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [showFallback, setShowFallback] = useState(false);

  // Handle OAuth callback when we land back on /buyer/settings#integrations
  useEffect(() => {
    const url = new URL(window.location.href);
    const sp = url.searchParams;
    const code = sp.get("code"), state = sp.get("state"), shop = sp.get("shop");
    if (code && state && shop) {
      api.get(`/buyer/api/shopify/oauth/callback?code=${encodeURIComponent(code)}&state=${encodeURIComponent(state)}&shop=${encodeURIComponent(shop)}`)
        .then(r => {
          toast.success(r.data?.mock ? "Shopify connected (mock mode)" : "Shopify connected");
          setSupplierId(r.data.seller_id);
        })
        .catch(e => toast.error(e.response?.data?.detail?.message || "OAuth failed"))
        .finally(() => {
          // Strip the OAuth params from the URL
          url.searchParams.delete("code");
          url.searchParams.delete("state");
          url.searchParams.delete("shop");
          window.history.replaceState({}, "", url.toString());
        });
    }
  }, []);

  useEffect(() => {
    if (connections.length > 0 && !supplierId) setSupplierId(connections[0].seller_id);
  }, [connections, supplierId]);

  const refresh = () => {
    if (!supplierId) return;
    api.get(`/buyer/api/shopify/${supplierId}`).then(r => setInstalled(r.data?.enabled ? r.data : null));
  };
  useEffect(refresh, [supplierId]);

  const installFallback = async () => {
    setBusy(true);
    try {
      const r = await api.post("/buyer/api/shopify/install", { seller_id: supplierId, ...config });
      setInstalled(r.data);
      toast.success("Shopify connector saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Install failed"); }
    finally { setBusy(false); }
  };

  const startOauth = async () => {
    if (!oauthShop) { toast.error("Enter your .myshopify.com domain"); return; }
    setBusy(true);
    try {
      const redirect_uri = `${window.location.origin}/buyer/settings`;
      const r = await api.post("/buyer/api/shopify/oauth/start", {
        seller_id: supplierId, shop_domain: oauthShop, redirect_uri,
      });
      // Redirect the browser to the Shopify install URL — for mock mode
      // this loops back to our redirect_uri with code/state baked in.
      window.location.href = r.data.url;
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't start OAuth"); setBusy(false); }
  };

  const runSync = async () => {
    setSyncing(true);
    try {
      const r = await api.post(`/buyer/api/shopify/${supplierId}/sync`);
      toast.success(`Synced ${r.data?.synced || 0} product${r.data?.synced === 1 ? "" : "s"}${r.data?.mock ? " (mock)" : ""}`);
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail?.message || "Sync failed"); }
    finally { setSyncing(false); }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect this Shopify store?")) return;
    try {
      await api.delete(`/buyer/api/shopify/${supplierId}`);
      setInstalled(null);
      toast.success("Disconnected");
    } catch (e) { toast.error(e.response?.data?.detail || "Disconnect failed"); }
  };

  return (
    <div className="space-y-4" data-testid="dev-shopify">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="display text-xl">Shopify connector</h2>
          <p className="text-[12px] text-foreground/55">Mirror your supplier&apos;s catalog into your Shopify store. One-click OAuth · pricing rules applied · auto-syncs every 6h.</p>
        </div>
        <select value={supplierId} onChange={(e) => setSupplierId(e.target.value)}
          data-testid="dev-shopify-supplier"
          className="h-10 rounded-md border border-border bg-white px-3 text-sm">
          {connections.map(c => <option key={c.seller_id} value={c.seller_id}>{c.tenant_name}</option>)}
        </select>
      </div>

      <div className="rounded-2xl border border-border bg-white p-6">
        <div className="flex items-start gap-4">
          <span className="w-11 h-11 rounded-xl bg-foreground text-background grid place-items-center shrink-0"><ShoppingBag className="w-5 h-5"/></span>
          <div className="flex-1 min-w-0">
            <div className="font-medium">{installed ? "Shopify connected" : "Connect a Shopify store"}</div>
            {installed ? (
              <div className="text-[12px] text-foreground/65 mt-1 space-y-1">
                <div>Shop: <span className="mono">{installed.config?.shop_domain}</span> · Status: <strong>{installed.last_status || "ready"}</strong>{installed.config?.mock && <span className="ml-1 chip bg-amber-50 border border-amber-200 text-amber-800 text-[9.5px]">mock</span>}</div>
                <div>Last sync: <span className="mono">{installed.last_sync_at ? new Date(installed.last_sync_at).toLocaleString() : "never"}</span>{installed.last_sync_count != null && <span className="ml-2">· {installed.last_sync_count} products</span>}</div>
              </div>
            ) : (
              <div className="text-[12px] text-foreground/65 mt-1">Connect via OAuth — Shopify will prompt you to authorize Verqo to read + write your products. Mirroring runs automatically every 6h once connected.</div>
            )}
          </div>
          {installed && (
            <div className="flex gap-2">
              <Button onClick={runSync} disabled={syncing} variant="outline" data-testid="dev-shopify-sync"
                className="rounded-full h-9 px-3 text-xs">
                {syncing ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin"/> : <RefreshCw className="w-3.5 h-3.5 mr-1"/>}
                Sync now
              </Button>
              <Button onClick={disconnect} variant="outline" className="rounded-full h-9 px-3 text-xs hover:bg-rose-50 hover:text-rose-700 hover:border-rose-200">
                Disconnect
              </Button>
            </div>
          )}
        </div>

        {!installed && (
          <div className="mt-5 space-y-3">
            <div className="grid sm:grid-cols-[1fr_auto] gap-3 items-end">
              <div>
                <Label>Your Shopify store domain</Label>
                <Input value={oauthShop} onChange={(e) => setOauthShop(e.target.value)}
                  placeholder="acme.myshopify.com" className="mt-1.5 mono"
                  data-testid="dev-shopify-domain"/>
              </div>
              <Button onClick={startOauth} disabled={busy || !oauthShop || !supplierId}
                data-testid="dev-shopify-oauth-start"
                className="rounded-full bg-foreground text-background h-10 px-5">
                {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin"/> : <ShoppingBag className="w-3.5 h-3.5 mr-1.5"/>}
                Install with Shopify
              </Button>
            </div>

            <button onClick={() => setShowFallback(v => !v)}
              className="text-[11.5px] text-foreground/60 hover:text-foreground underline-offset-2 hover:underline"
              data-testid="dev-shopify-fallback-toggle">
              {showFallback ? "Hide" : "Show"} manual setup (paste a Custom App token)
            </button>

            {showFallback && (
              <div className="rounded-xl border border-dashed border-border p-4 bg-secondary/30">
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label>Shop domain</Label>
                    <Input value={config.shop_domain} onChange={(e) => setConfig(c => ({ ...c, shop_domain: e.target.value }))}
                      placeholder="acme.myshopify.com" className="mt-1.5 mono"/>
                  </div>
                  <div>
                    <Label>Admin API access token</Label>
                    <Input type="password" value={config.access_token}
                      onChange={(e) => setConfig(c => ({ ...c, access_token: e.target.value }))}
                      placeholder="shpat_…" className="mt-1.5 mono"/>
                  </div>
                  <Button onClick={installFallback} disabled={busy || !config.shop_domain || !config.access_token}
                    className="rounded-full bg-foreground text-background col-span-2">
                    {busy ? "Saving…" : "Save Custom App token"}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────── Playground tab ─────────────────────── */
const PLAYGROUND_PRESETS = [
  { id: "list-products",  label: "List products",           method: "GET",  path: "/catalog/products?limit=10&with_margin=1" },
  { id: "get-product",    label: "Get single product",      method: "GET",  path: "/catalog/products/SBX-BEAUTY-001" },
  { id: "list-categories",label: "List categories",         method: "GET",  path: "/catalog/categories" },
  { id: "list-changes",   label: "Delta sync (updates)",    method: "GET",  path: "/catalog/changes?since=2026-01-01T00:00:00Z" },
  { id: "create-order",   label: "Submit a PO (sandbox)",   method: "POST", path: "/orders",
    body: { lines: [{ sku: "SBX-BEAUTY-001", qty: 12 }], po_number: "PG-DEMO-001", terms: "net30" } },
  { id: "list-orders",    label: "List my orders",          method: "GET",  path: "/orders?limit=5" },
];

function PlaygroundPanel({ connections }) {
  const [keys, setKeys] = useState([]);
  const [selectedKeyId, setSelectedKeyId] = useState("");
  const [preset, setPreset] = useState(PLAYGROUND_PRESETS[0].id);
  const [method, setMethod] = useState("GET");
  const [path, setPath] = useState(PLAYGROUND_PRESETS[0].path);
  const [bodyText, setBodyText] = useState("");
  const [response, setResponse] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/buyer/api/keys").then(r => {
      const list = (r.data?.keys || []).filter(k => !k.revoked_at);
      setKeys(list);
      if (!selectedKeyId && list.length > 0) {
        // Prefer a test key if one exists — sandbox is the safest playground default.
        const test = list.find(k => k.env === "test");
        setSelectedKeyId(test ? test.id : list[0].id);
      }
    });
  }, []);

  useEffect(() => {
    const p = PLAYGROUND_PRESETS.find(x => x.id === preset);
    if (!p) return;
    setMethod(p.method);
    setPath(p.path);
    setBodyText(p.body ? JSON.stringify(p.body, null, 2) : "");
    setResponse(null);
  }, [preset]);

  const send = async () => {
    if (!selectedKeyId) { toast.error("Select an API key first"); return; }
    setBusy(true);
    setResponse(null);
    let parsedBody = null;
    if (method === "POST" && bodyText.trim()) {
      try { parsedBody = JSON.parse(bodyText); }
      catch (_e) { toast.error("Body is not valid JSON"); setBusy(false); return; }
    }
    try {
      const r = await api.post("/buyer/api/playground/run", {
        api_key_id: selectedKeyId, method, path, body: parsedBody,
      });
      setResponse(r.data);
    } catch (e) {
      setResponse({
        status_code: e.response?.status || 0,
        body: e.response?.data || { error: String(e) },
        headers: {},
      });
    } finally { setBusy(false); }
  };

  const selectedKey = keys.find(k => k.id === selectedKeyId);

  const curlExample = (() => {
    const base = `${process.env.REACT_APP_BACKEND_URL || ""}/api/v1/integrations`;
    const auth = selectedKey ? `${selectedKey.key_prefix}…${selectedKey.last_four}` : "vbk_live_…";
    if (method === "POST") {
      return `curl -X POST '${base}${path}' \\\n  -H "Authorization: Bearer ${auth}" \\\n  -H "Content-Type: application/json" \\\n  -d '${bodyText.replace(/\n/g, " ")}'`;
    }
    return `curl '${base}${path}' \\\n  -H "Authorization: Bearer ${auth}"`;
  })();

  return (
    <div className="space-y-4" data-testid="dev-playground">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="display text-xl">API playground</h2>
          <p className="text-[12px] text-foreground/55">
            Try every endpoint live, without writing a line of code. Pick a key, pick a preset, hit send.
            {keys.some(k => k.env === "test") && <span className="ml-1 text-amber-700">Test keys serve a deterministic sandbox catalog (SBX-* SKUs).</span>}
          </p>
        </div>
      </div>

      {keys.length === 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-[12px] text-amber-900">
          <AlertTriangle className="inline w-3.5 h-3.5 mr-1"/>
          You don&apos;t have any API keys yet. <a href="#" onClick={(e) => { e.preventDefault(); window.dispatchEvent(new CustomEvent("dev-jump", { detail: "keys" })); }} className="underline">Create one in the API keys tab</a> to use the playground.
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Request column */}
        <div className="rounded-2xl border border-border bg-white p-5 space-y-3">
          <div>
            <Label>API key</Label>
            <select value={selectedKeyId} onChange={(e) => setSelectedKeyId(e.target.value)}
              data-testid="pg-key-select"
              className="mt-1.5 w-full h-10 rounded-md border border-input bg-white px-3 text-sm">
              <option value="">— pick a key —</option>
              {keys.map(k => {
                const supplier = connections.find(c => c.seller_id === k.seller_id);
                return (
                  <option key={k.id} value={k.id}>
                    {k.name} · {supplier?.tenant_name || k.seller_id.slice(0,6)} · {k.env}
                  </option>
                );
              })}
            </select>
            {selectedKey?.env === "test" && (
              <div className="mt-1.5 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1 inline-block">
                <Zap className="inline w-3 h-3 mr-1"/>Sandbox mode — responses come from synthetic SBX-* products
              </div>
            )}
          </div>

          <div>
            <Label>Preset</Label>
            <select value={preset} onChange={(e) => setPreset(e.target.value)}
              data-testid="pg-preset-select"
              className="mt-1.5 w-full h-10 rounded-md border border-input bg-white px-3 text-sm">
              {PLAYGROUND_PRESETS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
          </div>

          <div className="grid grid-cols-[auto_1fr] gap-2">
            <div>
              <Label>Method</Label>
              <select value={method} onChange={(e) => setMethod(e.target.value)}
                className="mt-1.5 h-10 rounded-md border border-input bg-white px-3 text-sm mono">
                <option>GET</option>
                <option>POST</option>
              </select>
            </div>
            <div>
              <Label>Path</Label>
              <Input value={path} onChange={(e) => setPath(e.target.value)}
                data-testid="pg-path"
                className="mt-1.5 mono text-[12px]" placeholder="/catalog/products?limit=10"/>
            </div>
          </div>

          {method === "POST" && (
            <div>
              <Label>Body (JSON)</Label>
              <textarea value={bodyText} onChange={(e) => setBodyText(e.target.value)}
                data-testid="pg-body"
                className="mt-1.5 w-full min-h-[140px] rounded-md border border-input bg-white px-3 py-2 text-[12px] mono leading-relaxed"
                placeholder='{"lines":[{"sku":"SBX-BEAUTY-001","qty":12}]}'/>
            </div>
          )}

          <Button onClick={send} disabled={busy || !selectedKeyId}
            data-testid="pg-send"
            className="w-full rounded-full bg-foreground text-background hover:bg-foreground/90 h-10">
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin"/> : <Play className="w-3.5 h-3.5 mr-1.5"/>}
            Send request
          </Button>

          <details className="text-[11px] text-foreground/60">
            <summary className="cursor-pointer">View cURL equivalent</summary>
            <pre className="mt-2 rounded-lg bg-foreground text-background p-3 overflow-x-auto text-[10.5px] mono leading-relaxed whitespace-pre-wrap">{curlExample}</pre>
          </details>
        </div>

        {/* Response column */}
        <div className="rounded-2xl border border-border bg-white p-5 space-y-2 min-h-[420px]" data-testid="pg-response">
          <div className="flex items-center justify-between">
            <div className="font-medium text-sm">Response</div>
            {response && (
              <div className="flex items-center gap-2">
                <span className={`chip text-[10.5px] mono ${response.status_code < 300 ? "bg-emerald-50 border-emerald-200 text-emerald-700" : response.status_code < 500 ? "bg-amber-50 border-amber-200 text-amber-800" : "bg-rose-50 border-rose-200 text-rose-700"}`}>
                  {response.status_code}
                </span>
                {response.duration_ms != null && <span className="text-[10.5px] text-foreground/55">{response.duration_ms}ms</span>}
              </div>
            )}
          </div>
          {!response && (
            <div className="text-center text-foreground/40 text-[12px] py-16">
              <Terminal className="w-7 h-7 mx-auto opacity-50 mb-2"/>
              Response will appear here.
            </div>
          )}
          {response && (
            <>
              {response.headers && Object.keys(response.headers).length > 0 && (
                <details className="text-[11px]">
                  <summary className="cursor-pointer text-foreground/55">Headers</summary>
                  <pre className="mt-1 rounded-md bg-secondary/40 p-2 overflow-x-auto mono text-[10.5px]">{Object.entries(response.headers).map(([k, v]) => `${k}: ${v}`).join("\n")}</pre>
                </details>
              )}
              <pre className="rounded-lg bg-foreground text-background p-3 overflow-auto text-[11px] mono leading-relaxed max-h-[440px]" data-testid="pg-response-body">
                {JSON.stringify(response.body, null, 2)}
              </pre>
              <Button onClick={() => { navigator.clipboard.writeText(JSON.stringify(response.body, null, 2)); toast.success("Response copied"); }}
                variant="outline" className="w-full rounded-full text-xs h-9">
                <Copy className="w-3.5 h-3.5 mr-1.5"/>Copy response
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


/* ─────────────────────── Docs tab ────────────────────────────── */
function DocsPanel() {
  const base = `${process.env.REACT_APP_BACKEND_URL || ""}/api/v1/integrations`;
  return (
    <div className="space-y-6" data-testid="dev-docs">
      <div>
        <h2 className="display text-xl">API reference</h2>
        <p className="text-[12.5px] text-foreground/65 mt-1">Base URL: <code className="mono px-1.5 py-0.5 rounded bg-secondary">{base}</code></p>
        <div className="mt-3 rounded-xl bg-secondary/40 border border-border p-3 text-[11.5px] text-foreground/70">
          <strong className="text-foreground">Available resource families:</strong>
          {" "}<span className="chip bg-emerald-50 border border-emerald-200 text-emerald-700 text-[9.5px] ml-1">Catalog · live</span>
          {" "}<span className="chip bg-emerald-50 border border-emerald-200 text-emerald-700 text-[9.5px]">Orders · live</span>
          {" "}<span className="chip bg-secondary text-[9.5px]">Invoices · coming Q2</span>
          {" "}<span className="chip bg-secondary text-[9.5px]">Payments · coming Q3</span>
          {" "}<span className="chip bg-secondary text-[9.5px]">RFQ · coming Q3</span>
          <div className="mt-1.5 text-foreground/55">All families share the same auth + key + webhook system. Add a key once, use it everywhere.</div>
        </div>
        <div className="mt-3 rounded-xl bg-amber-50 border border-amber-200 p-3 text-[11.5px] text-amber-900">
          <Zap className="inline w-3.5 h-3.5 mr-1"/>
          <strong>Sandbox mode:</strong> create an API key with env <span className="mono">test</span> (prefix <span className="mono">vbk_test_</span>) — every request will return a deterministic synthetic catalog of <span className="mono">SBX-*</span> products + orders, perfect for building your integration without touching real supplier data.
        </div>
      </div>

      <DocSection title="Auth" code={`Authorization: Bearer vbk_live_<your-key>`} desc="All requests require a bearer API key. Generate one in the API keys tab (one key per supplier)."/>

      <DocSection title="List products" method="GET" path="/catalog/products?limit=50&with_margin=1"
        desc="Cursor-paginated. Add ?with_margin=1 to get your_retail_price calculated server-side."
        code={`curl ${base}/catalog/products \\
  -H "Authorization: Bearer vbk_live_..."`}/>

      <DocSection title="Single product" method="GET" path="/catalog/products/{sku}"
        code={`curl ${base}/catalog/products/BRD-001 \\
  -H "Authorization: Bearer vbk_live_..."`}/>

      <DocSection title="Delta sync" method="GET" path="/catalog/changes?since=ISO"
        desc="Returns products updated since the given timestamp — perfect for daily sync."
        code={`curl "${base}/catalog/changes?since=2026-01-01T00:00:00Z" \\
  -H "Authorization: Bearer vbk_live_..."`}/>

      <DocSection title="Submit a PO" method="POST" path="/orders"
        desc="Idempotency-Key header recommended. Below-MOQ orders rejected with 400."
        code={`curl -X POST ${base}/orders \\
  -H "Authorization: Bearer vbk_live_..." \\
  -H "Content-Type: application/json" \\
  -H "Idempotency-Key: unique-uuid-here" \\
  -d '{
    "lines": [
      {"sku": "BRD-001", "qty": 24},
      {"sku": "BRD-002", "qty": 50}
    ],
    "po_number": "PO-ERP-001",
    "terms": "net30"
  }'`}/>

      <DocSection title="List your orders" method="GET" path="/orders?status=pending"
        code={`curl "${base}/orders?status=pending" \\
  -H "Authorization: Bearer vbk_live_..."`}/>

      <DocSection title="Webhook verification" desc="Recompute HMAC and compare with v1 in the X-Verqo-Signature header."
        code={`# Python
import hmac, hashlib
sig = request.headers["X-Verqo-Signature"]
t, v1 = dict(p.split("=") for p in sig.split(","))["t"], dict(p.split("=") for p in sig.split(","))["v1"]
expected = hmac.new(SECRET.encode(), f"{t}.{body}".encode(), hashlib.sha256).hexdigest()
assert hmac.compare_digest(v1, expected)`}/>

      <div className="rounded-xl bg-secondary/50 border border-border p-4 text-[11.5px] text-foreground/65">
        <Activity className="inline w-3 h-3 mr-1"/>
        Rate limits — Free: 1000 req/hr · Pro: 10k req/hr · Enterprise: 100k req/hr. Current usage shown in <span className="mono">X-RateLimit-*</span> response headers.
      </div>
    </div>
  );
}

function DocSection({ title, method, path, desc, code }) {
  return (
    <div className="rounded-xl border border-border bg-white p-4">
      <div className="flex items-baseline gap-2">
        {method && <span className={`chip text-[10px] mono uppercase tracking-wider ${method === "POST" ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-blue-50 border-blue-200 text-blue-700"}`}>{method}</span>}
        <div className="font-medium">{title}</div>
        {path && <code className="ml-auto mono text-[11.5px] text-foreground/65">{path}</code>}
      </div>
      {desc && <p className="text-[12px] text-foreground/65 mt-1.5">{desc}</p>}
      {code && (
        <pre className="mt-3 rounded-lg bg-foreground text-background p-3 overflow-x-auto text-[11.5px] mono leading-relaxed">{code}</pre>
      )}
    </div>
  );
}

function Th({ children, right }) { return <th style={{ textAlign: right ? "right" : "left" }} className="text-[10px] uppercase tracking-[0.18em] mono font-medium py-2.5 px-3">{children}</th>; }
function Td({ children, right }) { return <td style={{ textAlign: right ? "right" : "left", verticalAlign: "middle" }} className="py-2.5 px-3">{children}</td>; }
